import { inject, Injectable } from '@angular/core';
import { ImporterService } from '@app/core/services';
import { isoStringDateFormat } from '@app/core/tools/date.tools';
import { AppActions, ImportsActions } from '@app/store/actions';
import * as fromRoot from '@app/store/reducers';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { isEmpty, isEqual } from 'lodash';
import moment from 'moment';
import { merge, of, throwError } from 'rxjs';
import { catchError, delay, map, switchMap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class ImportsEffects {
  readonly actions$ = inject(Actions);
  readonly store = inject<Store<fromRoot.State>>(Store);
  readonly importerService = inject(ImporterService);
  readonly translocoService = inject(TranslocoService);

  getAllImportsDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImportsActions.getAllImportsDates),
      withLatestFrom(this.store.select(fromRoot.getAllImportsDates)),
      switchMap(([_, previousImportsDates]) =>
        this.importerService.getAllLatestImports().pipe(
          switchMap(allImportsDates => {
            const actions: Array<Action<string>> = [ImportsActions.getAllImportsDatesSuccess({ allImportsDates })];
            const entitiesNeedUpdate =
              Object.values(previousImportsDates).find(date => !!date) &&
              !isEqual(previousImportsDates, allImportsDates);
            if (entitiesNeedUpdate) {
              const updateEntitiesAt = this._getUpdateEntitiesAt();
              actions.push(AppActions.setUpdateEntitiesAt({ updateEntitiesAt }));
            }
            return actions;
          }),
          catchError(error =>
            this.translocoService.selectTranslate('error.latest-import', {}, 'admin').pipe(
              switchMap(errorLabel => {
                this.store.dispatch(ImportsActions.getAllImportsDatesFailure({ errorLabel }));
                return throwError(error);
              })
            )
          )
        )
      )
    )
  );

  getOneImportDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImportsActions.getOneImportDate),
      switchMap(action =>
        this.importerService.getLatestImport(action.uploadedFile).pipe(
          map(date => ImportsActions.getOneImportDateSuccess({ uploadedFile: action.uploadedFile, date })),
          catchError(error =>
            this.translocoService.selectTranslate('error.latest-import', {}, 'admin').pipe(
              switchMap(errorLabel => {
                this.store.dispatch(
                  ImportsActions.getOneImportDateFailure({ uploadedFile: action.uploadedFile, errorLabel })
                );
                return throwError(error);
              })
            )
          )
        )
      )
    )
  );

  importFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImportsActions.importFile),
      switchMap(action => {
        return this.importerService.importFile(action.file, action.uploadedFile).pipe(
          switchMap(response => {
            if (isEmpty(response.errorMessages)) {
              const updateEntitiesAt = this._getUpdateEntitiesAt();
              return [
                ImportsActions.importFileSuccess({ uploadedFile: action.uploadedFile }),
                AppActions.setUpdateEntitiesAt({ updateEntitiesAt }),
              ];
            }
            return [ImportsActions.importFileFailure({ uploadedFile: action.uploadedFile })];
          }),
          catchError(error => {
            this.store.dispatch(ImportsActions.importFileFailure({ uploadedFile: action.uploadedFile }));
            return throwError(error);
          })
        );
      })
    )
  );

  importFileSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImportsActions.importFileSuccess),
      switchMap(action =>
        merge(
          of(ImportsActions.getOneImportDate({ uploadedFile: action.uploadedFile })),
          of(ImportsActions.setRecentlyImported({ uploadedFile: action.uploadedFile, recentlyImported: false })).pipe(
            delay(this._recentlyImportedDelay)
          )
        )
      )
    )
  );

  private readonly _recentlyImportedDelay = 5000;
  private readonly _updateEntitiesAtDelay = 10000;

  private _getUpdateEntitiesAt(): string {
    return moment().add(this._updateEntitiesAtDelay).format(isoStringDateFormat);
  }
}
